import type { AccountActions } from "@/account/account-actions";
import type { IVaultMedia } from "@/account/account-models";
import type { ChatsActions } from "@/chats/chats-actions";
import type { IFileWrapper } from "@/common/uploader/uploader-helpers";
import type { IMediaType } from "@/models";
import type { MessengerActions } from "./messenger-actions";

export const CHAT_CLOSED_ERROR_MESSAGE = "Chat was closed";
export const CHAT_BLOCKED_ERROR_MESSAGE =
  "Sorry, this user has ended the chat.";
export const CHAT_BLOCKED_COUNTRY_ERROR_MESSAGE =
  "Sorry this creator not available in your country";
export const CHAT_ACCOUNT_NO_LONGER_EXISTS_ERROR_MESSAGE =
  "Sorry, this account no longer exists.";
export const CHAT_FROZEN_ERROR_MESSAGE_USER =
  "Your account has been temporarily frozen. Please respond to your chat with alua support.";
export const CHAT_FROZEN_ERROR_MESSAGE_MODEL =
  "Your account has been temporarily frozen. Please respond to your chat with alua support or your account manager.";
export const NO_CREDITS_ERROR_MESSAGE = "You have no credits."; // special handling for corner cases
export const CALLS_NOT_ACCEPTED_ERROR_MESSAGE =
  "This user is not accepting calls.";
export const MESSAGE_ALREADY_PURCHASED_ERROR = "Message was already purchased.";
export const CONTENT_ALREADY_PURCHASED_ERROR = "Content already purchased.";
export const BALANCE_TOO_LOW_ERROR = "Balance too low.";
export const SENDING_MEDIA_BLOCKED = "Sending media blocked.";
export const CHAT_MESSAGE_INVALID_JSON_UNKNOWN_JSON_ENCODING_DECODING_ERROR =
  "Invalid JSON data in request body: Unknown JSON encoding/decoding error.";
export const CHAT_MESSAGE_INVALID_JSON_SINGLE_UNPAIRED_UTF16_SURROGATE_ERROR =
  "Invalid JSON data in request body: Single unpaired UTF-16 surrogate in unicode escape";

export enum EMessageType {
  // --- normal ---
  Text = "text",
  Image = "image",
  Video = "video",
  Gif = "gif",
  TipUser = "tip_user",
  TipModel = "tip_model",
  // --- system ---
  // ChatStarted = 'chat_started', // deprecated
  // ChatEnded = 'chat_ended', // deprecated
  // CreditRateUpdated = 'credit_rate_updated', // not used?
  // ScreenshotTaken = 'screenshot_taken', // do we need this on web?
  IntroEnded = "intro_ended", // for user
  IntroEndedModel = "intro_ended_model",
  ForModel = "for_model", // new paid chat with fan
  ForBothModels = "for_both_models", // new chat with both models, also for both users
  ForModelIntro = "for_model_intro", // new paid chat with not a fan
  ForModelRateChanged = "for_model_rate_changed",
  ForModelCallRateChanged = "for_model_call_rate_changed", // the creator's own note, creator only
  ForModelSubscription = "for_model_subscription",
  ForUser = "for_user", // new paid chat with model as fan
  ForUserRateChanged = "for_user_rate_changed",
  ForUserCallRateChanged = "for_user_call_rate_changed", // the fan's note of a call rate change, not sent by the backend yet
  ForUserIntro = "for_user_intro", // new paid chat with model as not a fan
  ForUserSubscription = "for_user_subscription",
  // PrivateFeedBanner = 'private_feed_banner', // not used?
  // --- Paid Chat v2 system messages ---
  ForUserPaidChatDisabled = "for_user_paid_chat_disabled",
  ForModelPaidChatDisabled = "for_model_paid_chat_disabled",
  ForUserPaidChatEnabled = "for_user_paid_chat_enabled",
  ForModelPaidChatEnabled = "for_model_paid_chat_enabled",
  ForUserPaidChatRequested = "for_user_paid_chat_requested",
  ForModelPaidChatRequested = "for_model_paid_chat_requested",
  ForUserPaidChatAccepted = "for_user_paid_chat_accepted",
  ForModelPaidChatAccepted = "for_model_paid_chat_accepted",
  ForUserPaidChatDeclined = "for_user_paid_chat_declined",
  ForModelPaidChatDeclined = "for_model_paid_chat_declined",
  ForUserPaidChatCanceled = "for_user_paid_chat_canceled",
  ForModelPaidChatCanceled = "for_model_paid_chat_canceled",
  ForUserChatSetFree = "for_user_chat_set_free",
  ForModelChatSetFree = "for_model_chat_set_free",
  // --- Video calls system messages ---
  CallEnded = "call_ended",
  CallMissed = "call_missed",
  CallRejected = "call_rejected",
  CallCanceled = "call_canceled",
  // --- client side ---
  Typing = "typing",
}

export interface IMessageCallInfo {
  call_id: string;
  duration?: number;
  credits_spent?: number;
  earned_credits?: number;
}

export enum ECallStatus {
  Created = 1,
  Answered = 2,
  Active = 3,
  Ended = 4,
  Canceled = 5,
  Rejected = 6,
  Missed = 7,
  Failed = 8,
}

export enum ECallEventType {
  Incoming = "call_incoming",
  Accepted = "call_accepted",
  Rejected = "call_rejected",
  Canceled = "call_canceled",
  Missed = "call_missed",
  Ended = "call_ended",
  Failed = "call_failed",
  BalanceUpdated = "call_balance_updated",
}

export interface IServerCallClock {
  elapsed_seconds: number;
  received_at: number;
}

export interface ICallInfo {
  _id: string;
  call_id: string;
  chat_id: string;
  status: ECallStatus;
  status_label: string;
  is_incoming?: boolean;
  rate: number;
  channel_name: string;
  app_id: string;
  uid: number;
  token: string | null;
  duration: number;
  // How long the call has been connected when this was sent, so a restored
  // call carries on from there instead of from zero (Trello #8362).
  elapsed_seconds?: number;
  // Set by this app, not the API: the server's elapsed time and when it
  // arrived. It exists only once the server has said billing is running.
  server_clock?: IServerCallClock;
  credits_spent: number;
  earned_credits: number;
  tip_total_credits: number;
  // Paid by card: still held, charged so far (final once ended), and the card.
  billing_method?: "credits" | "usd";
  held_cents?: number;
  charged_cents?: number;
  card_last_4?: string;
  // True while the fan or the creator is out of the video: the server is not
  // billing, so the timer stands still.
  billing_paused?: boolean;
  // True in the last 60 seconds of held time, when the next hold did not go
  // through: the call ends once the current hold is used up.
  low_credits?: boolean;
  fan: ICallParticipant;
  model: ICallParticipant;
  created_at: string;
}

// Who is on the call, as the API snapshots it on the call. The incoming-call
// popup and call screens show this when the chat is not in the chat list, so
// it needs the display name and the avatar with image URLs (th/st), not just
// the username and avatar public id (Trello #8321).
export interface ICallParticipant {
  _id: string | { $oid: string };
  username: string;
  display_name?: string;
  avatar?: Partial<IMediaType>;
  // Their online status when the call data was made (always false for
  // invisible accounts).
  is_online?: boolean;
}

// The banner over the call announcing a tip, shown wherever the user is in the
// app. `text` is left out when the other person's name should be used.
export interface ICallTipNotice {
  id: string;
  chat_id?: string;
  text?: string;
  amount: string;
  note?: string;
}

export interface ICallTokenInfo {
  call_id: string;
  channel_name: string;
  uid: number;
  token: string;
  app_id: string;
  expires_in: number;
}

export interface IMessageInfo {
  _id?: string;
  chat_id: string;
  credit_tip?: number; // only for credits
  sender_id: string;
  recipient_id: string;
  is_paid?: boolean;
  is_paid_by_credit?: boolean;
  type?: EMessageType;
  text?: string;
  tip?: number; // only for usd
  tipMedia?: IMediaType; // TODO: confirm from backend
  credits?: number; // earned or payed
  price?: number; // price to unlock
  price_in_dollars?: boolean; // price in usd instead of Credits
  created_at?: string;
  is_read?: boolean;
  read_at?: string;
  is_system?: boolean;
  self_destruct?: boolean;
  self_destruct_hours?: number | null;
  destruct_at?: string; // ?
  image?: IMediaType;
  video?: IMediaType;
  video_length?: number;
  new_rate?: number; // https://trello.com/c/V2oOylcG/1-show-users-cr-rate-on-system-messages
  // client side properties
  isRevealed?: boolean;
  error?: { message: string };
  clientSideReceiveId?: string;
  __clientSideSendId?: string;
  // Keeps the message's list key stable when the sent message replaces its
  // client-side copy, so the bubble is updated in place instead of remounted.
  clientSideKey?: string;
  mediaFileWrapper?: IFileWrapper;
  vaultMedia?: IVaultMedia;
  uploadProgress?: number;
  loaded?: number;
  total?: number;
  abortUpload?: () => void;
  md5?: string;
  media_id?: string;
  gif?: {
    gif_url: string;
    height: number;
    width: number;
  };
  is_tips_message?: boolean;
  parent_message_id?: string;
  parent_message?: IMessageInfo;
  is_deleted?: boolean;
  is_hidden?: boolean;
  // Paid Chat v2 fields
  unlock_price?: number; // credits to unlock a locked text message (separate from `price` to avoid PPV-media confusion)
  blur?: boolean; // true when this text message is locked and should render blurred
  is_locked?: boolean; // true while not yet unlocked by the fan
  chars?: number; // real character count, sent alongside unlock_price — used to size the locked bubble accurately
  call?: IMessageCallInfo;
  reactions?: Record<string, string[]>; // { "👍": ["userId1", "userId2"], "❤️": ["userId3"] }
}

export interface IGetFromLastIdMessagesConfig {
  chat_id: string;
  // recipient_id: string;
  // sender_id: string;
  last_message_id: string;
  // clientSideReceiveId?: string; // timestamp in milliseconds
  unread_messages: number;
  actions: MessengerActions;
  accountActions: AccountActions;
  chatsActions: ChatsActions;
}

export interface IUpdateMessageAsPaidConfig {
  messageId: string;
  destructAt: string;
}

export interface IDestructibleMessage {
  message_id: string;
  destruct_at: string;
}

export interface IUpdateMessagesReadConfig {
  messageIds: string[];
  chatId: string;
  recipientId: string;
  destructibles: IDestructibleMessage[];
  timestampString: string;
}

export interface IMessageMutationError {
  message?: string;
}
